import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Pagination } from '../components/ui/pagination';
import {
  Plus, Search, FileText, Calendar, Banknote, Grid3x3, List, Send, CheckCircle, Award, XCircle,
  Loader2, AlertCircle, MessageSquareReply, Ban,
} from 'lucide-react';
import { rfqAPI } from '../lib/api';
import type { RFQListItem, RFQDashboardStats } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useIsCustomer } from '../hooks/usePortalMode';
import { usePagination } from '../hooks/usePagination';
import RFQCreateWizard from '../components/rfq/RFQCreateWizard';
import { isUntouchedDraft } from '../components/rfq/useRfqWizardDraft';
import { useRfqMoney } from '../components/rfq/useRfqMoney';
import { isDeadlinePassed } from '../components/rfq/rfqResponseState';

// The customer tabs are the DERIVED workflow states (see
// backend/rfq/workflow_state.py). The supplier tabs are the viewer's own
// POSITION — what they still owe, what they are waiting on, how it ended —
// never the RFQ's stored status, which describes the buyer's process and
// would tell a supplier how many competitors have quoted. Every RFQ a
// supplier can see sits in exactly one of them (`supplier_position_q`).
type CustomerTab = 'all' | 'draft' | 'awaiting_quotes' | 'ready_to_award' | 'no_quotes' | 'awarded' | 'cancelled';
type SupplierTab = 'all' | 'to_quote' | 'revise' | 'quoted' | 'won' | 'lost' | 'closed';
type StatusTab = CustomerTab | SupplierTab;

const EMPTY_STATS: RFQDashboardStats = {
  total: 0, draft: 0, sent: 0, submitted: 0, awarded: 0, cancelled: 0,
  awaiting_quotes: 0, ready_to_award: 0, no_quotes: 0,
  to_quote: 0, revise: 0, quoted: 0, won: 0, lost: 0, closed: 0,
};
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  awarded: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-300 text-gray-700',
  // Derived states. "Ready to Award" is amber because it is the only one
  // asking the buyer to do something; "Closed — No Quotes" is red because it
  // is a dead end that needs chasing or re-issuing, not a quiet resting state.
  awaiting_quotes: 'bg-blue-100 text-blue-800',
  ready_to_award: 'bg-amber-100 text-amber-800',
  no_quotes: 'bg-red-100 text-red-800',
  // Supplier positions. Purple is the "in negotiation" colour the response
  // card already uses for a revision round; MISSED and LOST share the muted
  // grey because neither asks the supplier for anything.
  quoted: 'bg-emerald-100 text-emerald-800',
  revision_requested: 'bg-purple-100 text-purple-800',
  missed: 'bg-gray-200 text-gray-700',
};
const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

/** Display labels for the derived states. Mirrors WORKFLOW_STATE_LABELS in
 *  backend/rfq/workflow_state.py — keep the two in step. */
const WORKFLOW_LABELS: Record<string, string> = {
  draft: 'DRAFT',
  awaiting_quotes: 'AWAITING QUOTES',
  ready_to_award: 'READY TO AWARD',
  no_quotes: 'NO QUOTES',
  awarded: 'AWARDED',
  cancelled: 'CANCELLED',
};

type TabDef = { key: StatusTab; label: string; icon: typeof FileText | null; color: string };

// "Sent" and "Submitted" both described the wrong thing. "Sent" is the
// buyer's own act, not a state of play, and "Submitted" was a threshold flag
// meaning every invited supplier had quoted — it read as though the CUSTOMER
// submitted something, and it left the two states a buyer most needs
// unexpressible: a deadline passed with three of four quotes in, and a
// deadline passed with none at all. Both used to sit under "Sent", where an
// RFQ nobody ever answered looked identical to one issued this morning.
const CUSTOMER_TABS: TabDef[] = [
  { key: 'all', label: 'Total', icon: null, color: '' },
  { key: 'draft', label: 'Draft', icon: FileText, color: 'text-gray-600' },
  { key: 'awaiting_quotes', label: 'Awaiting Quotes', icon: Send, color: 'text-blue-600' },
  { key: 'ready_to_award', label: 'Ready to Award', icon: CheckCircle, color: 'text-amber-600' },
  { key: 'no_quotes', label: 'No Quotes', icon: AlertCircle, color: 'text-red-600' },
  { key: 'awarded', label: 'Awarded', icon: Award, color: 'text-green-600' },
  { key: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'text-gray-400' },
];

// The buying side's own words do not describe the supplier's position. "Sent"
// is the buyer's act; to the supplier it is a quote they still owe. "Draft" is
// a state they can never see at all — an unsent RFQ is not shared — so that
// tab sat there permanently reading 0.
//
// "Awarded" was the damaging one: it is the RFQ's status, not the supplier's
// outcome, so a supplier who LOST still found the job under a green "Awarded"
// heading. The row badge below now says WON or LOST instead.
//
// The tabs used to filter on that stored status too, so a supplier who had
// quoted still found the job under "To Quote" until every competitor had.
// They now filter on the viewer's own position (`position=`), counted per
// supplier by the backend — see `supplier_position_q` — and read left to
// right as the life of a quote: what I owe, what I am waiting on, how it
// ended, what is over.
const SUPPLIER_TABS: TabDef[] = [
  { key: 'all', label: 'Total', icon: null, color: '' },
  { key: 'to_quote', label: 'To Quote', icon: Send, color: 'text-blue-600' },
  { key: 'revise', label: 'Revise', icon: MessageSquareReply, color: 'text-purple-600' },
  { key: 'quoted', label: 'Quoted', icon: CheckCircle, color: 'text-emerald-600' },
  { key: 'won', label: 'Won', icon: Award, color: 'text-green-600' },
  { key: 'lost', label: 'Lost', icon: XCircle, color: 'text-gray-500' },
  { key: 'closed', label: 'Closed', icon: Ban, color: 'text-gray-400' },
];

/** Icon for the row badge, keyed by the label the supplier actually reads. */
const SUPPLIER_LABEL_ICON_KEY: Record<string, string> = {
  'TO QUOTE': 'sent',
  'REVISE QUOTE': 'revise',
  'QUOTED': 'submitted',
  'WON': 'awarded',
  'LOST': 'lost',
  'MISSED': 'missed',
  'CANCELLED': 'cancelled',
};

/**
 * What the status badge on a row should say, for whoever is looking.
 *
 * The customer sees the RFQ's own status. A supplier sees their position in
 * it, which is only the same thing until an award: after that the RFQ reads
 * "awarded" to everyone, and the loser was being shown a green AWARDED badge
 * on a job they did not get.
 *
 * `awarded_supplier` is already redacted per viewer by the backend
 * (`rfq/serializers/_viewer.py`): the winner gets their own id, everyone else
 * gets null and the name "Another supplier". So its presence IS the verdict —
 * no extra field and no second request needed.
 */
export function rowStatusLabel(
  // The derived fields are optional here because the function already copes
  // without them (it falls back to `status` and renders no count), and a
  // caller holding a partially-built row should not be forced to invent them.
  rfq: Pick<RFQListItem, 'status' | 'awarded_supplier'>
    & Partial<Pick<RFQListItem, 'workflow_state' | 'response_count' | 'submitted_response_count' | 'my_response_status' | 'response_deadline'>>,
  isCustomer: boolean,
): { label: string; tone: string } {
  if (!isCustomer) {
    const won = { label: 'WON', tone: 'bg-green-100 text-green-800' };
    const lost = { label: 'LOST', tone: 'bg-gray-200 text-gray-700' };
    const quoted = { label: 'QUOTED', tone: STATUS_COLORS['quoted'] || 'bg-gray-100' };
    // Still owed, unless the deadline has gone — then it is a quote they
    // missed, not one they can still make (the form is read-only by then).
    const toQuote = () => (
      rfq.response_deadline && isDeadlinePassed({ status: rfq.status, response_deadline: rfq.response_deadline })
        ? { label: 'MISSED', tone: STATUS_COLORS['missed'] || 'bg-gray-100' }
        : { label: 'TO QUOTE', tone: STATUS_COLORS['sent'] || 'bg-gray-100' }
    );
    if (rfq.status === 'awarded') return rfq.awarded_supplier !== null ? won : lost;
    if (rfq.status === 'cancelled') return { label: 'CANCELLED', tone: STATUS_COLORS['cancelled'] || 'bg-gray-100' };
    // The supplier's own position, from `my_response_status`. The RFQ's
    // status cannot say it: "sent" covers a supplier who quoted an hour ago,
    // and "submitted" only means every competitor has — which is also why a
    // supplier row must never fall through to the buyer's counted badge.
    switch (rfq.my_response_status) {
      case 'submitted': return quoted;
      case 'revision_requested':
        return { label: 'REVISE QUOTE', tone: STATUS_COLORS['revision_requested'] || 'bg-gray-100' };
      case 'won': return won;
      case 'lost': return lost;
      case 'pending':
      case 'in_progress': return toQuote();
      default: break;
    }
    // No position on the row (an older payload, a partial row): the stored
    // status can still say "outstanding" or "everyone has quoted".
    if (rfq.status === 'submitted') return quoted;
    if (rfq.status === 'sent') return toQuote();
    return { label: rfq.status.toUpperCase(), tone: STATUS_COLORS[rfq.status] || 'bg-gray-100' };
  }
  // The customer's badge reads the DERIVED state, and carries the count with
  // it: "3 of 4" versus "4 of 4" is what tells a buyer the field was
  // incomplete when time ran out. That distinction used to need its own
  // status; the count carries it, so one label covers one action.
  const state: string = rfq.workflow_state || rfq.status;
  const label = WORKFLOW_LABELS[state] || rfq.status.toUpperCase();
  const showCount = state === 'awaiting_quotes' || state === 'ready_to_award';
  return {
    label: showCount
      ? `${label} · ${rfq.submitted_response_count ?? 0} of ${rfq.response_count ?? 0}`
      : label,
    tone: STATUS_COLORS[state] || 'bg-gray-100',
  };
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'draft': return <FileText className="h-3 w-3" />;
    case 'sent': return <Send className="h-3 w-3" />;
    case 'submitted': return <CheckCircle className="h-3 w-3" />;
    case 'awarded': return <Award className="h-3 w-3" />;
    case 'cancelled': return <XCircle className="h-3 w-3" />;
    // Supplier-only badges (see SUPPLIER_LABEL_ICON_KEY).
    case 'revise': return <MessageSquareReply className="h-3 w-3" />;
    case 'lost': return <XCircle className="h-3 w-3" />;
    case 'missed': return <Ban className="h-3 w-3" />;
    default: return null;
  }
}

/** Copy for the empty list: "first RFQ" only when the tenant has none at all. */
export function emptyListMessage(totalUnfiltered: number, hasSearch: boolean, statusTab: StatusTab): string {
  if (totalUnfiltered === 0 && !hasSearch) return 'No RFQs found. Create your first RFQ to get started.';
  if (hasSearch) return 'No RFQs match your search.';
  if (statusTab !== 'all') return 'No RFQs match this filter.';
  return 'No RFQs found.';
}

export default function RFQ() {
  const { formatDate } = useFormatters();
  const formatMoney = useRfqMoney();
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const statTabs = isCustomer ? CUSTOMER_TABS : SUPPLIER_TABS;
  const [rfqs, setRfqs] = useState<RFQListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [stats, setStats] = useState<RFQDashboardStats>(EMPTY_STATS);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const pagination = usePagination({ pageSize: PAGE_SIZE });
  const { currentPage, setCurrentPage, setFromResponse } = pagination;

  // Debounce typing so each keystroke doesn't hit the API. The page reset
  // happens in the same batch as the search change so the list never asks
  // for a stale page number with the new filter (DRF 404s on an out-of-range
  // page).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = searchTerm.trim();
        if (next !== prev) setCurrentPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchTerm, setCurrentPage]);

  const changeStatusTab = useCallback((tab: StatusTab) => {
    // A supplier has no Draft tab; if one is reached any other way (a stale
    // link, a restored tab), fall back to All rather than filter on a status
    // they can never hold and show an empty list with no tab highlighted.
    const allowed = (isCustomer ? CUSTOMER_TABS : SUPPLIER_TABS).some(t => t.key === tab);
    setStatusTab(allowed ? tab : 'all');
    setCurrentPage(1);
  }, [setCurrentPage, isCustomer]);

  const fetchRFQs = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page: currentPage, page_size: PAGE_SIZE };
      if (statusTab !== 'all') {
        // Customer tabs are derived states and go to `workflow_state`; the
        // supplier tabs are the viewer's own position and go to `position`.
        // `status` would match neither and the backend would return the whole
        // list rather than erroring.
        params[isCustomer ? 'workflow_state' : 'position'] = statusTab;
      }
      if (debouncedSearch) params['search'] = debouncedSearch;
      const response = await rfqAPI.getRFQs(params);
      const data = response.data;
      if (Array.isArray(data)) {
        setRfqs(data);
        setFromResponse(data.length);
      } else {
        setRfqs(data.results ?? []);
        setFromResponse(data.count ?? 0);
      }
    } catch (error) {
      console.error('Error fetching RFQs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusTab, debouncedSearch, setFromResponse, isCustomer]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await rfqAPI.getDashboardStats();
      setStats({ ...EMPTY_STATS, ...response.data });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => { fetchRFQs(); }, [fetchRFQs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleCreate = () => {
    setShowCreateModal(false);
    fetchRFQs();
    fetchStats();
  };

  // CR-5468 rework: the wizard auto-saves progress server-side, but nothing
  // outside the wizard said so. QA filled in the first page, saw "Draft
  // saved", left, and found no trace of it anywhere — the only way back was
  // to press Create RFQ again and notice the resume prompt. Surfacing it here
  // gives the saved progress a home on the page people actually look at.
  const [wizardDraftAt, setWizardDraftAt] = useState<string | null>(null);
  useEffect(() => {
    if (!isCustomer) return;
    let cancelled = false;
    rfqAPI.getDraft()
      .then((res) => {
        if (cancelled) return;
        // 204 = no draft; the endpoint returns no body in that case. A blank
        // draft (left by the pre-CR-6207 wizard) is nothing to resume.
        const data = res.status === 204 ? null : res.data;
        setWizardDraftAt(data && !isUntouchedDraft(data.wizard_state) ? data.updated_at ?? null : null);
      })
      .catch(() => { /* non-fatal — the banner simply doesn't show */ });
    return () => { cancelled = true; };
  }, [isCustomer, showCreateModal]);

  if (showCreateModal) {
    return <RFQCreateWizard onClose={() => setShowCreateModal(false)} onCreate={handleCreate} />;
  }

  const statValue = (key: StatusTab) => (key === 'all' ? stats.total : stats[key]) || 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">RFQ Management</h1>
          <p className="text-muted-foreground">
            {isCustomer ? 'Manage Requests for Quotation and track supplier responses' : 'Requests for Quotation you have been invited to respond to'}
          </p>
        </div>
        {isCustomer && (
          <Button onClick={() => setShowCreateModal(true)} data-tour="create-rfq-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create RFQ
          </Button>
        )}
      </div>

      {isCustomer && wizardDraftAt && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-primary" />
              <span>
                You have unfinished RFQ progress from{' '}
                {formatDate(wizardDraftAt, { includeTime: true })} — it has not been created as an RFQ yet.
              </span>
            </div>
            <Button size="sm" onClick={() => setShowCreateModal(true)}>Resume</Button>
          </CardContent>
        </Card>
      )}

      <div className={`grid grid-cols-2 gap-4 ${isCustomer ? 'md:grid-cols-6' : 'md:grid-cols-4 lg:grid-cols-7'}`}>
        {statTabs.map(({ key, label, icon: Icon, color }) => (
          <Card key={key} className={statusTab === key ? 'border-primary' : ''}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => changeStatusTab(key)}>
              <CardDescription className="flex items-center gap-1">
                {Icon && <Icon className="h-3 w-3" />} {label}
              </CardDescription>
              <CardTitle className={`text-2xl ${color}`}>{statValue(key)}</CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <Tabs value={statusTab} onValueChange={(value) => changeStatusTab(value as StatusTab)}>
        <TabsList>
          <TabsTrigger value="all">All RFQs</TabsTrigger>
          {statTabs
            .filter(({ key }) => key !== 'all')
            .map(({ key, label }) => (
              <TabsTrigger key={key} value={key}>{label}</TabsTrigger>
            ))}
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by RFQ number or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="flex gap-1">
          <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('list')} aria-label="List view">
            <List className="h-4 w-4" />
          </Button>
          <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('grid')} aria-label="Grid view">
            <Grid3x3 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div data-tour="rfq-table" className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
        {loading ? (
          <div className="col-span-full text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">Loading RFQs...</p>
          </div>
        ) : rfqs.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">{emptyListMessage(stats.total, Boolean(debouncedSearch), statusTab)}</p>
            </CardContent>
          </Card>
        ) : (
          rfqs.map((rfq) => {
            const badge = rowStatusLabel(rfq, isCustomer);
            const iconKey = isCustomer ? rfq.status : (SUPPLIER_LABEL_ICON_KEY[badge.label] ?? rfq.status);
            return (
            <Card key={rfq.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/rfq/${rfq.id}`)}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{rfq.rfq_number}</CardTitle>
                      <Badge className={badge.tone}>
                        {getStatusIcon(iconKey)}
                        <span className="ml-1">{badge.label}</span>
                      </Badge>
                      <Badge className={PRIORITY_COLORS[rfq.priority] || 'bg-gray-100'}>{rfq.priority.toUpperCase()}</Badge>
                    </div>
                    <CardDescription className="font-medium text-base text-foreground">{rfq.title}</CardDescription>
                  </div>
                  {viewMode === 'list' && (
                    <div className="text-right space-y-1 ml-4">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Due: {formatDate(rfq.response_deadline)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm font-semibold">
                        <Banknote className="h-4 w-4" />
                        <span>{formatMoney(rfq.total_estimated_value, rfq.currency)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
                  <span>Category: <span className="font-medium text-foreground capitalize">{rfq.category}</span></span>
                  {viewMode === 'grid' && (
                    <span>Value: <span className="font-medium text-foreground">{formatMoney(rfq.total_estimated_value, rfq.currency)}</span></span>
                  )}
                  <span className="ml-auto">Created: {formatDate(rfq.created_at)}</span>
                </div>
              </CardContent>
            </Card>
            );
          })
        )}
      </div>

      {!loading && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          itemsPerPage={pagination.pageSize}
          onPageChange={pagination.goToPage}
          itemLabel="RFQs"
        />
      )}
    </div>
  );
}
